import { AdminPermission } from "../api/generated/models/AdminPermission";
import type { AdminNavSection } from "../config/adminNav";

export interface PermissionOption {
  permission: AdminPermission;
  label: string;
}

export interface PermissionGroup {
  id: string;
  label: string;
  options: PermissionOption[];
}

// The permission picker mirrors the sidebar: one group per sidebar group, in
// sidebar order, with one option per row that carries a permission.
export const collectPermissionGroups = (
  sections: AdminNavSection[]
): PermissionGroup[] =>
  sections.flatMap((section) =>
    section.items.flatMap((item) => {
      if (item.kind !== "group") return [];
      const options = item.items.flatMap((leaf) =>
        leaf.permission
          ? [{ permission: leaf.permission, label: leaf.permissionLabel ?? leaf.label }]
          : []
      );
      return options.length > 0 ? [{ id: item.id, label: item.label, options }] : [];
    })
  );

// Every permission with its display name: Super Admin first, then sidebar order.
export const listPermissionOptions = (
  groups: PermissionGroup[],
  superAdminLabel: string
): PermissionOption[] => [
  { permission: AdminPermission.SUPER_ADMIN, label: superAdminLabel },
  ...groups.flatMap((group) => group.options),
];

// Labels for an admin's permissions as the backend sent them, in picker order.
// A name the dashboard does not know is shown as-is, last, rather than dropped.
export const labelPermissions = (
  permissions: string[],
  options: PermissionOption[]
): { key: string; label: string }[] => {
  const position = (permission: string) => {
    const index = options.findIndex((option) => option.permission === permission);
    return index === -1 ? options.length : index;
  };
  return [...permissions]
    .sort((a, b) => position(a) - position(b))
    .map((permission) => ({
      key: permission,
      label:
        options.find((option) => option.permission === permission)?.label ??
        permission,
    }));
};

// The grant rules below mirror the backend (CreateAdminAccountCommand and
// UpdateAdminAccountPermissionsCommand), which enforces them regardless.

const holdsSuperAdmin = (permissions: readonly string[]) =>
  permissions.includes(AdminPermission.SUPER_ADMIN);

// Why the logged-in admin (the grantor) cannot grant `permission`, or null if
// they can. The backend checks every permission in the request, so this also
// applies to permissions the edited admin already holds.
export const grantBlockReason = (
  permission: AdminPermission,
  grantor: readonly string[]
): string | null => {
  if (permission === AdminPermission.SUPER_ADMIN) {
    return "Only support can grant Super Admin";
  }
  if (holdsSuperAdmin(grantor)) return null;
  if (permission === AdminPermission.ADMIN_MANAGEMENT) {
    return "Only Super Admins can grant this";
  }
  if (!grantor.includes(permission)) return "You don't have this permission";
  return null;
};

// A non-Super Admin never sees Admins (AdminManagement) in the picker.
export const visiblePermissionGroups = (
  groups: PermissionGroup[],
  grantor: readonly string[]
): PermissionGroup[] =>
  holdsSuperAdmin(grantor)
    ? groups
    : groups
        .map((group) => ({
          ...group,
          options: group.options.filter(
            (option) => option.permission !== AdminPermission.ADMIN_MANAGEMENT
          ),
        }))
        .filter((group) => group.options.length > 0);

// Why the logged-in admin cannot change this admin's permissions at all, or
// null if they can.
export const editBlockReason = (
  target: { id?: string; permissions: readonly string[] },
  editor: { id: string; permissions: readonly string[] }
): string | null => {
  if (target.id && target.id.toLowerCase() === editor.id.toLowerCase()) {
    return "You cannot change your own permissions.";
  }
  if (holdsSuperAdmin(target.permissions)) {
    return "Super Admins cannot be edited. Please contact support if you need to modify this account.";
  }
  if (
    !holdsSuperAdmin(editor.permissions) &&
    target.permissions.includes(AdminPermission.ADMIN_MANAGEMENT)
  ) {
    return "Only Super Admins can edit admins who have the Admins permission.";
  }
  return null;
};

export type GroupCheckState = "checked" | "unchecked" | "indeterminate";

// A group's "Select all" box acts on the options the admin can select. It is
// checked once all of those are selected, and indeterminate while anything in
// the group is selected short of that.
export const groupCheckState = (
  group: PermissionGroup,
  selected: readonly AdminPermission[],
  canSelect: (permission: AdminPermission) => boolean
): GroupCheckState => {
  const isSelected = (option: PermissionOption) =>
    selected.includes(option.permission);
  const selectable = group.options.filter((option) => canSelect(option.permission));
  // With nothing selectable (a read-only form) the box just reports the group.
  const scope = selectable.length > 0 ? selectable : group.options;
  if (scope.every(isSelected)) return "checked";
  return group.options.some(isSelected) ? "indeterminate" : "unchecked";
};

// Clears the group's selectable options when its box is checked, and selects
// all of them otherwise. Options the admin cannot select are left as they are.
export const toggleGroup = (
  group: PermissionGroup,
  selected: readonly AdminPermission[],
  canSelect: (permission: AdminPermission) => boolean
): AdminPermission[] => {
  const selectable = group.options
    .map((option) => option.permission)
    .filter(canSelect);
  if (groupCheckState(group, selected, canSelect) === "checked") {
    return selected.filter((permission) => !selectable.includes(permission));
  }
  return [
    ...selected,
    ...selectable.filter((permission) => !selected.includes(permission)),
  ];
};
